/**
 * Shared formatting utilities used across the Admin module.
 */

/**
 * Relative time from an ISO timestamp to now.
 * Returns strings like "just now", "15m ago", "3h ago", "2d ago", "3mo ago",
 * or falls back to a locale date string for very old timestamps.
 */
export function relativeTime(iso: string): string {
  const diff = Date.now() - new Date(iso).getTime();
  const seconds = Math.floor(diff / 1000);
  if (seconds < 60) return seconds < 1 ? 'just now' : `${seconds}s ago`;
  const minutes = Math.floor(seconds / 60);
  if (minutes < 60) return `${minutes}m ago`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours}h ago`;
  const days = Math.floor(hours / 24);
  if (days < 30) return `${days}d ago`;
  if (days < 365) return `${Math.floor(days / 30)}mo ago`;
  return new Date(iso).toLocaleDateString();
}

/**
 * Human-readable uptime from seconds.
 */
export function formatUptime(seconds: number): string {
  if (seconds === 0) return 'just started';
  const days = Math.floor(seconds / 86400);
  const hours = Math.floor((seconds % 86400) / 3600);
  if (days > 0) return `${days}d ${hours}h`;
  const minutes = Math.floor((seconds % 3600) / 60);
  return `${hours}h ${minutes}m`;
}

/**
 * Format bytes with the largest sensible unit.
 */
export function formatBytes(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  if (bytes < 1024 * 1024 * 1024) return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  return `${(bytes / (1024 * 1024 * 1024)).toFixed(2)} GB`;
}

/**
 * Format a duration in milliseconds.
 */
export function formatDuration(ms: number): string {
  const s = Math.round(ms / 1000);
  if (s < 60) return `${s}s`;
  const m = Math.floor(s / 60);
  const rs = s % 60;
  if (m < 60) return rs === 0 ? `${m}m` : `${m}m ${rs}s`;
  const h = Math.floor(m / 60);
  const rm = m % 60;
  return rm === 0 ? `${h}h` : `${h}h ${rm}m`;
}

/**
 * Format megabytes with GB rollover.
 */
export function formatMb(mb: number): string {
  if (mb >= 1024) return `${(mb / 1024).toFixed(1)} GB`;
  return `${mb} MB`;
}

/**
 * Render a date-only string (YYYY-MM-DD) in the user's locale WITHOUT
 * any timezone shift.
 *
 * Closes #182. The previous code did `new Date(s).toLocaleDateString()`,
 * which parses YYYY-MM-DD as UTC midnight per ECMA-262. In any tz with
 * a negative UTC offset (e.g. America/Chicago = UTC-5), the resulting
 * Date renders as the previous calendar day. Typing 2026-05-07 in a
 * Start Date picker and reloading would display "5/6/2026" — admin-
 * confusing behaviour that was filed as a major bug in round-3.
 *
 * Implementation:
 *   - YYYY-MM-DD strings are split and reconstructed in local time so
 *     the calendar day matches what the admin typed.
 *   - Strings with a 'T' (full ISO timestamps) are passed through to
 *     the regular Date parser — those are tz-aware and render
 *     correctly.
 *   - Other shapes are returned verbatim (defensive default).
 *
 * Use this anywhere a date-only column from the API is rendered:
 *   - users.start_date / users.end_date (bA1 migration)
 *   - any future DATE column on a user-facing model.
 *
 * Datetime fields (timestamp with tz) should continue to use
 * `new Date(iso).toLocaleDateString()` directly — those carry tz
 * information and don't suffer the off-by-one bug.
 */
export function formatDateOnly(value: string | null | undefined): string {
  if (!value) return '—';
  // Full ISO timestamp — parse normally, render date portion. Safe in
  // any tz because the input carries an explicit zone.
  if (value.includes('T')) {
    return new Date(value).toLocaleDateString();
  }
  // YYYY-MM-DD shape. Construct in LOCAL tz so the rendered day
  // matches the input regardless of the browser's offset.
  const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(value);
  if (match) {
    const [, y, m, d] = match;
    const local = new Date(Number(y), Number(m) - 1, Number(d));
    return local.toLocaleDateString();
  }
  // Unknown shape — return verbatim. Better than throwing or returning '—'.
  return value;
}

/**
 * Returns a comparable Date for a date-only value. Same tz semantics
 * as `formatDateOnly` — local-day, not UTC-midnight. Use when you
 * need to compare a YYYY-MM-DD value to `Date.now()` (e.g. "is this
 * end-date in the next 30 days?").
 */
export function parseDateOnly(value: string | null | undefined): Date | null {
  if (!value) return null;
  if (value.includes('T')) {
    return new Date(value);
  }
  const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(value);
  if (match) {
    const [, y, m, d] = match;
    return new Date(Number(y), Number(m) - 1, Number(d));
  }
  return null;
}

/**
 * Today's date in the browser's LOCAL timezone, in YYYY-MM-DD shape.
 *
 * Closes #250 (Failure A: Start Date forward shift). The previous
 * default for the Create User wizard's Start Date field was
 * `new Date().toISOString().slice(0, 10)`, which returns the UTC
 * calendar day. In a tz west of UTC (e.g. America/Chicago) during
 * the late evening, that produces the *next* local day — so the
 * wizard opens with tomorrow's date already filled in, and the
 * admin's typed value has to override the wrong default. If the
 * admin (or QA automation) doesn't actually override, the wrong
 * date gets persisted and surfaces as a "+1 day" bug on reload.
 *
 * Returning the LOCAL calendar day matches what the picker UI and
 * the formatDateOnly() output show, eliminating the discrepancy.
 *
 * NB: this is the inverse of `new Date().toISOString().slice(0, 10)`
 * — never use that pattern for date-only defaults.
 */
export function todayLocalIso(): string {
  const now = new Date();
  const yyyy = now.getFullYear().toString().padStart(4, '0');
  const mm = (now.getMonth() + 1).toString().padStart(2, '0');
  const dd = now.getDate().toString().padStart(2, '0');
  return `${yyyy}-${mm}-${dd}`;
}

/**
 * Normalise an arbitrary date-shaped value to YYYY-MM-DD without
 * any timezone shift, or return null when the value is empty or
 * unparseable.
 *
 * Closes #250 (Failure A + Failure B). Shape rules:
 *
 *   - null / undefined / ''             → null
 *   - 'YYYY-MM-DD'                      → returned verbatim
 *   - 'YYYY-MM-DDT...' (ISO timestamp)  → first 10 chars (UTC calendar
 *                                         date — matches what the API's
 *                                         server-side formatDate() emits
 *                                         for date-only columns)
 *   - Date object                       → UTC y/m/d (matches drizzle's
 *                                         PgDateString.mapFromDriverValue)
 *   - anything else                     → null (defensive)
 *
 * This is the canonical normaliser used at every API boundary on the
 * client: adapter (`api.startDate` → `user.startDate`), Edit dialog
 * (`user.startDate` → `<input type="date">` value), and PATCH body
 * comparison. Centralising the rule prevents `slice(0, 10)` drift
 * from spreading and makes it impossible for a Date object to leak
 * into the wire payload.
 *
 * IMPORTANT: never goes through `new Date(s).toISOString()` for
 * YYYY-MM-DD strings, because `new Date("2026-05-07")` is interpreted
 * as UTC midnight and `.toISOString()` then renders in UTC. That
 * round-trip is safe in UTC but shifts in any other zone — which is
 * the whole class of bugs #182 / #250 chase.
 */
export function normalizeDateOnly(
  value: string | Date | null | undefined
): string | null {
  if (value == null) return null;
  if (value instanceof Date) {
    if (Number.isNaN(value.getTime())) return null;
    const yyyy = value.getUTCFullYear().toString().padStart(4, '0');
    const mm = (value.getUTCMonth() + 1).toString().padStart(2, '0');
    const dd = value.getUTCDate().toString().padStart(2, '0');
    return `${yyyy}-${mm}-${dd}`;
  }
  if (typeof value !== 'string') return null;
  const trimmed = value.trim();
  if (trimmed === '') return null;
  // YYYY-MM-DD — already canonical.
  if (/^\d{4}-\d{2}-\d{2}$/.test(trimmed)) return trimmed;
  // ISO timestamp (YYYY-MM-DDTHH:MM:SS...) — slice the date portion.
  // This matches the API's server-side formatDate() which strips 'T...'
  // off of date-only column values.
  if (trimmed.length >= 10 && trimmed[10] === 'T' && /^\d{4}-\d{2}-\d{2}T/.test(trimmed)) {
    return trimmed.slice(0, 10);
  }
  return null;
}

/**
 * Has a date-only field changed?
 *
 * Both sides go through `normalizeDateOnly` first so the comparison
 * is shape-stable across:
 *   - YYYY-MM-DD strings (canonical wire format)
 *   - ISO timestamps with time component
 *   - Date objects (defensive — should never happen post-adapter,
 *     but the helper handles it in case raw API data leaks past)
 *   - empty string '' (form-input "not set" sentinel)
 *   - null / undefined (API "not set" sentinel)
 *
 * Returns true exactly when the canonical YYYY-MM-DD differs.
 *
 * Closes #250 round 7: the previous inline comparison in
 * `edit-user-dialog-v2.tsx` did
 *   `current !== (loaded?.slice(0, 10) ?? null)`
 * which compared the form's empty-string sentinel ('') against the
 * adapter's null sentinel and reported "dirty=true" even when the
 * field was unchanged. Re-using that same broken comparison shape
 * also made it possible for a typed "2026-12-31" change to read as
 * NOT-dirty if one side had drifted to a Date object during
 * background refetch (the round 7 reopen symptom).
 *
 * Centralising the comparison + sentinel collapse into a tested
 * helper closes both gaps and makes regression tests possible.
 */
export function isDateOnlyDirty(
  loaded: string | Date | null | undefined,
  current: string | Date | null | undefined
): boolean {
  // null/undefined/'' all collapse to null — the canonical "unset".
  return normalizeDateOnly(loaded) !== normalizeDateOnly(current);
}

/**
 * Summarise a raw user-agent string into a short Browser/OS label so
 * activity feeds read as an admin event log instead of a raw telemetry
 * dump.
 *
 * Closes #221: the user-detail Activity tab previously rendered raw or
 * truncated raw user-agent strings while the module-level `/users/activity`
 * feed used a clean summary. Both surfaces now share this helper so
 * the rendering is consistent across the module.
 *
 * Recognises the common Chromium / Firefox / Safari / curl / Edge
 * shapes; everything else falls back to the first whitespace-trimmed
 * 50 chars. Call sites should preserve the full UA on the element's
 * `title` attribute so admins can hover for the gory detail.
 */
export function summariseUserAgent(raw: string | null | undefined): string {
  if (!raw) return '';
  const ua = raw.trim();
  if (!ua) return '';

  const browser = (() => {
    if (/curl\//i.test(ua)) return `curl ${ua.match(/curl\/([^\s]+)/i)?.[1] ?? ''}`.trim();
    if (/Edg\//i.test(ua)) return `Edge ${ua.match(/Edg\/(\d+)/i)?.[1] ?? ''}`.trim();
    if (/Chrome\//i.test(ua) && !/Edg\//i.test(ua)) return `Chromium ${ua.match(/Chrome\/(\d+)/i)?.[1] ?? ''}`.trim();
    if (/Firefox\//i.test(ua)) return `Firefox ${ua.match(/Firefox\/(\d+)/i)?.[1] ?? ''}`.trim();
    if (/Safari\//i.test(ua)) return `Safari ${ua.match(/Version\/(\d+)/i)?.[1] ?? ''}`.trim();
    if (/Playwright/i.test(ua)) return 'Playwright';
    if (/Postman/i.test(ua)) return 'Postman';
    return null;
  })();

  const os = (() => {
    if (/Windows NT/i.test(ua)) return 'Windows';
    if (/Mac OS X/i.test(ua) && /Mobile/i.test(ua) === false) return 'macOS';
    if (/iPhone|iPad/i.test(ua)) return 'iOS';
    if (/Android/i.test(ua)) return 'Android';
    if (/Linux/i.test(ua)) return 'Linux';
    return null;
  })();

  if (browser && os) return `${browser} / ${os}`;
  if (browser) return browser;
  if (os) return os;
  return ua.length > 50 ? `${ua.slice(0, 47)}…` : ua;
}
